// Small formatting helpers — ported from the v5 prototype.
//
// Keep these pure so we can unit-test without DOM / time mocking
// theatrics. ``ageHuman`` accepts an optional ``now`` parameter for
// deterministic tests.

/** Compact human-readable "time since" — ``now`` / ``5 min`` / ``3h`` / ``2d``.
 *
 *  Returns ``'—'`` for null/undefined input so callers can pipe it
 *  directly into JSX without guards.
 */
export function ageHuman(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  const ms = now.getTime() - parsed.getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'сейчас';
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч`;
  const d = Math.floor(h / 24);
  return `${d} д`;
}

/** Bucket a 3-D residual (mm) into a colour class — matches the
 *  prototype thresholds. ``null``/``undefined`` resolves to ``'neutral'``. */
export function drClass(mm: number | null | undefined): 'good' | 'warn' | 'bad' | 'neutral' {
  if (mm == null) return 'neutral';
  if (mm < 5) return 'good';
  if (mm < 15) return 'warn';
  return 'bad';
}

/** Same thresholds, returns a CSS ``var(--...)`` literal so callers can
 *  pass it as an inline ``style={{ color: drColor(mm) }}``. */
export function drColor(mm: number | null | undefined): string {
  if (mm == null) return 'var(--ink-4)';
  if (mm < 5) return 'var(--good)';
  if (mm < 15) return 'var(--warn)';
  return 'var(--bad)';
}

/** Format a residual to one decimal place, falling back to an em-dash. */
export function fmtResidual(mm: number | null | undefined): string {
  if (mm == null || Number.isNaN(mm)) return '—';
  return mm.toFixed(1);
}

/** Format an ISO datetime as ``"DD.MM HH:mm"`` (24-hour, local TZ).
 *
 *  ``null``/invalid → ``"—"``. Used in the DetailScreen meta-row and
 *  the LogsScreen event timestamps where ageHuman is too imprecise.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format ``HH:mm:ss`` for compact log timestamps (one row per event). */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Phase 3.10.1 — ``HH:mm:ss`` in Moscow time (UTC+3), regardless of the
 *  user's device timezone.
 *
 *  Used by the DetailScreen timeline so a user opening the app from
 *  outside Russia still sees the timestamps in the timezone the
 *  scheduler operators care about. Backend always serializes UTC
 *  (Phase 3.10.1 Bug 1 — the ``Z`` suffix is enforced via Pydantic
 *  field serializers in ``api.models``) so this conversion is safe.
 *
 *  ``toLocaleString`` with explicit ``timeZone`` is the idiomatic
 *  cross-browser way to pin a date to a wall-clock zone without
 *  pulling in a heavy library like ``date-fns-tz``.
 */
export function formatTimeMoscow(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Render the distance between two ISO timestamps as ``"12 мин"`` /
 *  ``"3 ч 5 мин"`` / ``"2 д"``. Returns ``"—"`` if either side is
 *  missing or unparseable. ``end`` defaults to now — useful for
 *  rendering durations of jobs that are still running. */
export function formatDuration(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!startIso) return '—';
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return '—';
  const end = endIso ? new Date(endIso) : now;
  if (Number.isNaN(end.getTime())) return '—';
  const ms = end.getTime() - start.getTime();
  if (ms < 0) return '—';
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 1) return '<1 мин';
  if (totalMin < 60) return `${totalMin} мин`;
  const h = Math.floor(totalMin / 60);
  if (h < 24) {
    const m = totalMin - h * 60;
    return m === 0 ? `${h} ч` : `${h} ч ${m} мин`;
  }
  return `${Math.floor(h / 24)} д`;
}

/** Group items carrying ``ts`` into day-buckets for the Logs stream.
 *
 *  Buckets, in order:
 *  - ``"сегодня"`` — items whose ts is on today's calendar date.
 *  - ``"вчера"`` — yesterday.
 *  - ``"DD.MM.YYYY"`` for each older calendar date, newest first.
 *
 *  Mirrors the prototype's ``groupLogsByDay`` (lines 5817-5825). */
export function groupEventsByDay<T extends { ts: string }>(
  events: T[],
  now: Date = new Date(),
): Array<{ label: string; items: T[] }> {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86_400_000;
  const buckets = new Map<string, T[]>();
  const order: string[] = [];

  for (const ev of events) {
    const d = new Date(ev.ts);
    if (Number.isNaN(d.getTime())) continue;
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    let label: string;
    if (dayStart === today) label = 'сегодня';
    else if (dayStart === yesterday) label = 'вчера';
    else {
      const pad = (n: number) => String(n).padStart(2, '0');
      label = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    }
    if (!buckets.has(label)) {
      buckets.set(label, []);
      order.push(label);
    }
    buckets.get(label)!.push(ev);
  }
  return order.map((label) => ({ label, items: buckets.get(label)! }));
}

/** Phase 3.8 — time-range pills for the LogsScreen filter.
 *
 *  ``'all'`` returns ``undefined`` so the caller can spread it straight
 *  into the API filter object — the backend interprets no ``from`` as
 *  "no lower bound". ``'today'`` snaps to the local midnight so the
 *  pill follows the calendar day, not a 24-hour sliding window.
 */
export type TimeRange = 'hour' | 'today' | 'day' | 'week' | 'all';

export const TIME_RANGE_LABEL: Record<TimeRange, string> = {
  hour: 'час',
  today: 'сегодня',
  day: 'сутки',
  week: 'неделя',
  all: 'всё',
};

export const TIME_RANGES: readonly TimeRange[] = [
  'hour',
  'today',
  'day',
  'week',
  'all',
];

/** Convert a ``TimeRange`` pill to an ISO ``from`` query param value.
 *
 *  Returns ``undefined`` for ``'all'`` so the caller can drop it from
 *  the API filter object.
 */
export function rangeToFrom(
  range: TimeRange,
  now: Date = new Date(),
): string | undefined {
  switch (range) {
    case 'hour':
      return new Date(now.getTime() - 3_600_000).toISOString();
    case 'today': {
      const midnight = new Date(now);
      midnight.setHours(0, 0, 0, 0);
      return midnight.toISOString();
    }
    case 'day':
      return new Date(now.getTime() - 86_400_000).toISOString();
    case 'week':
      return new Date(now.getTime() - 7 * 86_400_000).toISOString();
    case 'all':
      return undefined;
  }
}

/** API → CSS class for the GNSS chip set. Maps the full names the
 *  backend stores (``glonass``, ``galileo``, ``beidou``) to the
 *  3-letter codes used in the prototype's CSS (``glo``, ``gal``,
 *  ``bds``). Unknown systems fall through unchanged. */
export function normalizeGnssCode(api: string): string {
  switch (api.toLowerCase()) {
    case 'glonass':
      return 'glo';
    case 'galileo':
      return 'gal';
    case 'beidou':
      return 'bds';
    default:
      return api.toLowerCase();
  }
}
